/**
 * Per-tab session scoping.
 *
 * One browser holds one session cookie, so without a tab-scoped id every tab shares a single
 * set of server session values. We mint an id into sessionStorage - which is natively per-tab
 * and survives a reload - and send it on every request so each tab owns its own session slot.
 *
 * See LISA.AppServer/docs/impersonate-user.md.
 */

const STORAGE_KEY = "lisa-tab-ctx";

export const TAB_CONTEXT_HEADER = "X-Lisa-Context-Id";

export const IMPERSONATE_PARAM = "impersonate_token";

let tabID:string = null;

function hasImpersonateToken():boolean{
    try {
        return new URLSearchParams(window.location.search).has(IMPERSONATE_PARAM);
    }
    catch(e){
        return false;
    }
}

function newTabID():string{
    let cryptoObj:any = typeof crypto !== "undefined" ? crypto : null;
    if (cryptoObj && cryptoObj.randomUUID){
        return cryptoObj.randomUUID();
    }
    // crypto.randomUUID needs a secure context - fall back so http dev hosts still work
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,(c) => {
        let r = Math.random() * 16 | 0;
        let v = c == "x" ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * The id for this tab, minted on first use and reused for the life of the tab.
 *
 * Note that a same-origin window.open clones the opener's sessionStorage into the new tab, so
 * a newly opened tab starts out holding its opener's id. Impersonation must therefore force a
 * fresh id rather than relying on the stored value being absent - see resetTabID.
 */
export function getTabID():string{
    if (tabID){
        return tabID;
    }
    // A same-origin window.open clones the opener's sessionStorage, so a tab opened to view the
    // portal as someone else starts out holding the opener's id and would otherwise share their
    // session slot - overwriting it on exchange. Presence of the token forces a fresh slot.
    if (hasImpersonateToken()){
        return resetTabID();
    }
    try {
        tabID = sessionStorage.getItem(STORAGE_KEY);
        if (!tabID){
            tabID = newTabID();
            sessionStorage.setItem(STORAGE_KEY,tabID);
        }
    }
    catch(e){
        // sessionStorage can be unavailable (private mode, embedded contexts). Fall back to an
        // id that lives only as long as this page load - the tab still gets its own session
        // slot, it just does not survive a reload.
        tabID = newTabID();
    }
    return tabID;
}

/**
 * Removes the impersonation token from the address bar once it has been exchanged. The token is
 * single use, so what this really prevents is it lingering in history or being copied out of a
 * shared url. Safe because the tab id in sessionStorage survives a reload, so the established
 * session is found again without it.
 */
export function stripImpersonateToken():void{
    try {
        if (!hasImpersonateToken()){
            return;
        }
        let url = new URL(window.location.href);
        url.searchParams.delete(IMPERSONATE_PARAM);
        window.history.replaceState({},document.title,url.pathname + url.search + url.hash);
    }
    catch(e){
        // history/URL unavailable - leaving the spent token in the bar is not harmful
    }
}

/**
 * Discards any inherited or stored id and mints a new one, so this tab gets a session slot of
 * its own. Used when a tab is opened to run as a different user.
 */
export function resetTabID():string{
    tabID = newTabID();
    try {
        sessionStorage.setItem(STORAGE_KEY,tabID);
    }
    catch(e){
        // see getTabID
    }
    return tabID;
}
